// apps/admin/src/features/billing/InvoiceFilters.tsx
//
// Phase 8.2 Wave 3 Task 11 — Filter bar extracted from
// `InvoiceListPage.tsx`. Owns the 6-state status filter (DRAFT / SENT /
// PAID / PARTIAL / OVERDUE / VOID per contract `InvoiceStatus` enum),
// merchant picker, period range, search box, and the "Overdue only"
// toggle (A1 acceptance criteria).
//
// Stateless — parent holds the canonical filter URL state and feeds the
// resolved values + onChange callbacks down. Keeps the page component
// thin and the filter UI unit-testable in isolation.
//
// vg-binding: PLAN:task-11
// vg-binding: P8.D-88
// vg-binding: P8.D-485
// vg-binding: BR-11

import type { JSX } from 'react';
import {
  Button,
  Card,
  CardContent,
  Input,
  MerchantPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import type { AdminInvoiceStatus } from '../../types/finance.js';

/** Single-select wrapper for the status dropdown (UX choice — contract
 * accepts `status[]`, but the A1 spec ships a single-status filter with
 * an "All" sentinel; multi-select is deferred to a future enrich). */
export type InvoiceStatusFilterValue = '' | AdminInvoiceStatus;

/** Radix sentinel for the "All statuses" option (maps to '' on the wire). */
const ALL_STATUS_VALUE = '__all__';

interface InvoiceFiltersProps {
  /** Current free-text search (committed value — input has its own local
   * draft state via parent). */
  search: string;
  /** Draft search box value (uncommitted). */
  searchDraft: string;
  onSearchDraftChange: (next: string) => void;
  /** Commit the search box (called on Enter / blur). */
  onSearchCommit: (next: string) => void;

  status: InvoiceStatusFilterValue;
  onStatusChange: (next: InvoiceStatusFilterValue) => void;

  merchantId: string;
  onMerchantChange: (id: string) => void;

  periodFrom: string;
  periodTo: string;
  onPeriodFromChange: (iso: string) => void;
  onPeriodToChange: (iso: string) => void;

  overdueOnly: boolean;
  onOverdueOnlyChange: (next: boolean) => void;

  /** True if at least one filter has a non-default value. */
  hasAnyFilter: boolean;
  onClearAll: () => void;

  /** Pre-bound overdue summary count from list meta — displayed inline
   * with the toggle so the user can see the impact before flipping it. */
  overdueCount?: number;
}

/**
 * Status filter values — order chosen for the A1 UX: lifecycle progression
 * (DRAFT → SENT → PARTIAL → PAID), then exception states (OVERDUE, VOID).
 * The empty-string sentinel maps to the "All statuses" option / undefined on
 * the wire. Display labels resolved via i18n (`invoice.statusLabels.*`).
 */
const STATUS_FILTER_VALUES: ReadonlyArray<AdminInvoiceStatus> = [
  'DRAFT',
  'SENT',
  'PARTIAL',
  'PAID',
  'OVERDUE',
  'VOID',
];

export function InvoiceFilters(props: InvoiceFiltersProps): JSX.Element {
  const { t } = useTranslation('finance');
  const {
    search,
    searchDraft,
    onSearchDraftChange,
    onSearchCommit,
    status,
    onStatusChange,
    merchantId,
    onMerchantChange,
    periodFrom,
    periodTo,
    onPeriodFromChange,
    onPeriodToChange,
    overdueOnly,
    onOverdueOnlyChange,
    hasAnyFilter,
    onClearAll,
    overdueCount,
  } = props;

  return (
    <Card>
      <CardContent
        className="flex flex-wrap items-end gap-3 py-4"
        data-testid="admin-invoices-filter-bar"
      >
        {/* Search box ----------------------------------------------------- */}
        <div className="min-w-[260px] flex-1">
          <label
            className="mb-1 block text-xs font-medium text-pw-text-muted"
            htmlFor="admin-invoices-search"
          >
            {t('invoice.filters.searchLabel')}
          </label>
          <Input
            id="admin-invoices-search"
            type="search"
            value={searchDraft}
            placeholder={t('invoice.filters.searchPlaceholder')}
            onChange={(e) => onSearchDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSearchCommit(searchDraft.trim());
            }}
            onBlur={() => {
              if (searchDraft.trim() !== search) {
                onSearchCommit(searchDraft.trim());
              }
            }}
            aria-label={t('invoice.filters.searchAria')}
            data-testid="admin-invoices-search-input"
          />
        </div>

        {/* Status filter -------------------------------------------------- */}
        <div className="min-w-[180px]">
          <label
            className="mb-1 block text-xs font-medium text-pw-text-muted"
            htmlFor="invoices-status-filter"
          >
            {t('invoice.filters.status')}
          </label>
          <Select
            value={status === '' ? ALL_STATUS_VALUE : status}
            onValueChange={(value) =>
              onStatusChange(
                value === ALL_STATUS_VALUE
                  ? ''
                  : (value as InvoiceStatusFilterValue),
              )
            }
          >
            <SelectTrigger
              id="invoices-status-filter"
              data-testid="invoices-status-filter"
              aria-label={t('invoice.filters.statusAria')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_STATUS_VALUE}>
                {t('invoice.filters.allStatuses')}
              </SelectItem>
              {STATUS_FILTER_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`invoice.statusLabels.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Merchant picker ----------------------------------------------- */}
        <div className="min-w-[220px]">
          <label
            className="mb-1 block text-xs font-medium text-pw-text-muted"
            htmlFor="admin-invoices-merchant-filter"
          >
            {t('invoice.filters.merchant')}
          </label>
          <MerchantPicker
            value={merchantId || null}
            valueLabel={null}
            onChange={(id) => onMerchantChange(id ?? '')}
            billingMode="INVOICE"
            placeholder={t('invoice.filters.merchantPlaceholder')}
            testId="admin-invoices-merchant-filter"
          />
        </div>

        {/* Period from/to ------------------------------------------------- */}
        <div className="min-w-[160px]">
          <label
            className="mb-1 block text-xs font-medium text-pw-text-muted"
            htmlFor="admin-invoices-period-from"
          >
            {t('invoice.filters.periodFrom')}
          </label>
          <Input
            id="admin-invoices-period-from"
            type="date"
            value={periodFrom}
            onChange={(e) => onPeriodFromChange(e.target.value)}
            aria-label={t('invoice.filters.periodFromAria')}
            data-testid="admin-invoices-period-from"
          />
        </div>
        <div className="min-w-[160px]">
          <label
            className="mb-1 block text-xs font-medium text-pw-text-muted"
            htmlFor="admin-invoices-period-to"
          >
            {t('invoice.filters.periodTo')}
          </label>
          <Input
            id="admin-invoices-period-to"
            type="date"
            value={periodTo}
            onChange={(e) => onPeriodToChange(e.target.value)}
            aria-label={t('invoice.filters.periodToAria')}
            data-testid="admin-invoices-period-to"
          />
        </div>

        {/* Overdue-only toggle + count chip ----------------------------- */}
        <label
          className="ml-1 inline-flex cursor-pointer items-center gap-2 text-sm text-pw-text"
          data-testid="admin-invoices-overdue-toggle"
        >
          <input
            type="checkbox"
            checked={overdueOnly}
            onChange={(e) => onOverdueOnlyChange(e.target.checked)}
            className="h-4 w-4 rounded border-pw-border accent-pw-primary"
          />
          <span>{t('invoice.filters.overdueOnly')}</span>
          {typeof overdueCount === 'number' && overdueCount > 0 ? (
            <span
              className="inline-flex items-center rounded-md border border-rose-200 dark:border-rose-800 bg-rose-100 dark:bg-rose-950/40 px-1.5 py-0.5 text-xs font-medium text-rose-800 dark:text-rose-300"
              data-testid="admin-invoices-overdue-chip"
              aria-label={t('invoice.filters.overdueChipAria', { count: overdueCount })}
            >
              {overdueCount}
            </span>
          ) : null}
        </label>

        {hasAnyFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            data-testid="admin-invoices-clear-filters"
          >
            {t('invoice.filters.clearFilters')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default InvoiceFilters;
